import type ExcelJS from 'exceljs'

const HEADER_AMBER = 'FFD97706'
const HEADER_PLUM = 'FF6B2C5F'
const HEADER_TEXT = 'FFFFFFFF'

export type TemplateColumnSpec = {
  header: string
  width: number
  accent?: 'amber' | 'plum'
  required?: boolean
  listValues?: readonly string[]
}

export type ImportTemplateSpec = {
  sheetName: string
  columns: readonly TemplateColumnSpec[]
  exampleRows: readonly (readonly string[])[]
  validationRows?: number
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const DEFAULT_VALIDATION_ROWS = 500

function headerText(column: TemplateColumnSpec): string {
  return column.required ? `${column.header}*` : column.header
}

function thinBorder(): ExcelJS.Borders {
  return {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
  } as ExcelJS.Borders
}

export async function buildImportTemplateWorkbook(spec: ImportTemplateSpec): Promise<Blob> {
  const { default: ExcelJSLib } = await import('exceljs')
  const workbook = new ExcelJSLib.Workbook()
  workbook.creator = 'Blind Book Command Center'
  workbook.created = new Date()
  workbook.modified = new Date()

  const sheet = workbook.addWorksheet(spec.sheetName)

  spec.columns.forEach((column, index) => {
    const cell = sheet.getCell(1, index + 1)
    cell.value = headerText(column)
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 11 }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: column.accent === 'plum' ? HEADER_PLUM : HEADER_AMBER },
    }
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    cell.border = thinBorder()
    sheet.getColumn(index + 1).width = column.width
  })
  sheet.getRow(1).height = 28

  spec.exampleRows.forEach((row, rowIndex) => {
    row.forEach((value, columnIndex) => {
      sheet.getCell(2 + rowIndex, columnIndex + 1).value = value
    })
  })

  const lastValidationRow = 1 + Math.max(spec.exampleRows.length, spec.validationRows ?? DEFAULT_VALIDATION_ROWS)
  spec.columns.forEach((column, index) => {
    if (!column.listValues || column.listValues.length === 0) return
    const formula = `"${column.listValues.join(',')}"`
    const allowed = column.listValues.join(', ')
    for (let row = 2; row <= lastValidationRow; row++) {
      sheet.getCell(row, index + 1).dataValidation = {
        type: 'list',
        allowBlank: !column.required,
        formulae: [formula],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: `Invalid ${column.header}`,
        error: `Choose one of: ${allowed}`,
      }
    }
  })

  sheet.views = [{ state: 'frozen', ySplit: 1 }]
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: spec.columns.length },
  }

  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], { type: XLSX_MIME })
}
